import { useTranslation } from 'module/hooks/use-translation';
import { Dropdown } from 'module/ui/components/atoms/dropdown/dropdown';
import { Form } from 'module/ui/components/atoms/form';
import {
  ToolbarButtonIcon,
  ToolbarDropdownTriggerButton,
} from 'module/ui/components/atoms/rich-text-editor/toolbar/components';
import { TableDataType } from 'module/ui/components/atoms/rich-text-editor/toolbar/table-toolbar';
import { faUserGroup, faArrowsToLine } from '@fortawesome/pro-solid-svg-icons';
import {
  answerFrom,
  AnswerLength,
} from 'api/custom-section/types/multi-question';
import { startCase } from 'lodash';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import type { Option } from '@rocketlane/ui';

const DropdownRow = styled.div`
  display: flex;
  gap: var(--spacing-04);
  width: 100%;
  align-items: flex-start;
  flex-direction: column;

  ${Form.Item} {
    width: fit-content;
    flex: 0;
  }
`;

const StyledTriggerButton = styled(ToolbarDropdownTriggerButton)`
  margin-left: 0 !important;
`;

interface AnswerDropdownsProps {
  option: any;
  editInputOption: (option: any) => void;
  setChoices?: (updater: (prev: any[]) => any[]) => void;
}

export const AnswerDropdowns: React.FC<AnswerDropdownsProps> = ({
  option,
  editInputOption,
  setChoices,
}) => {
  // Translation labels
  const customerLabel = useTranslation(
    'rich_text_editor.sections.answer_from.customer',
  );
  const vendorLabel = useTranslation(
    'rich_text_editor.sections.answer_from.vendor',
  );
  const allLabel = useTranslation('rich_text_editor.sections.answer_from.all');
  const twentyWordsLabel = useTranslation(
    'rich_text_editor.sections.answer_length.20',
  );
  const hundredWordsLabel = useTranslation(
    'rich_text_editor.sections.answer_length.100',
  );
  const twoHundredWordsLabel = useTranslation(
    'rich_text_editor.sections.answer_length.200',
  );
  const fiveHundredWordsLabel = useTranslation(
    'rich_text_editor.sections.answer_length.500',
  );

  // Answer Source options
  const answerFromOptions: Option[] = useMemo(
    () => [
      { value: answerFrom.Customer, label: customerLabel },
      { value: answerFrom.Vendor, label: vendorLabel },
      { value: answerFrom.All, label: allLabel },
    ],
    [customerLabel, vendorLabel, allLabel],
  );

  // Answer Length options
  const answerLengthOptions: Option[] = useMemo(
    () => [
      { value: AnswerLength.TwentyWords, label: twentyWordsLabel },
      { value: AnswerLength.HundredWords, label: hundredWordsLabel },
      { value: AnswerLength.TwoHundredWords, label: twoHundredWordsLabel },
      { value: AnswerLength.FiveHundredWords, label: fiveHundredWordsLabel },
    ],
    [
      twentyWordsLabel,
      hundredWordsLabel,
      twoHundredWordsLabel,
      fiveHundredWordsLabel,
    ],
  );

  const handleanswerFromChange = (sourceOption: Option) => {
    const updatedOption = {
      ...option,
      fieldOptionMeta: {
        ...option.fieldOptionMeta,
        answerFrom: sourceOption.value,
      },
    };

    editInputOption(updatedOption);

    if (setChoices) {
      setChoices((previous) =>
        previous.map((choice) =>
          choice.value === option.value
            ? {
                ...choice,
                fieldOptionMeta: {
                  ...choice.fieldOptionMeta,
                  answerFrom: sourceOption.value,
                },
              }
            : choice,
        ),
      );
    }
  };

  const handleAnswerLengthChange = (lengthOption: Option) => {
    const updatedOption = {
      ...option,
      fieldOptionMeta: {
        ...option.fieldOptionMeta,
        answerLength: lengthOption.value,
      },
    };

    editInputOption(updatedOption);

    if (setChoices) {
      setChoices((previous) =>
        previous.map((choice) =>
          choice.value === option.value
            ? {
                ...choice,
                fieldOptionMeta: {
                  ...choice.fieldOptionMeta,
                  answerLength: lengthOption.value,
                },
              }
            : choice,
        ),
      );
    }
  };

  return (
    <DropdownRow>
      <Form.Item
        label={<FormattedMessage id="rich_text_editor.sections.answer_from" />}
      >
        <Dropdown
          menu={{
            items: answerFromOptions.map((sourceOption) => ({
              key: sourceOption.value,
              label: sourceOption.label,
              onClick: () => handleanswerFromChange(sourceOption),
            })),
          }}
        >
          <StyledTriggerButton icon={<ToolbarButtonIcon icon={faUserGroup} />}>
            <FormattedMessage
              id="rich_text_editor.sections.type"
              values={{
                type: startCase(
                  answerFromOptions.find(
                    (opt) => opt.value === option.fieldOptionMeta?.answerFrom,
                  )?.label ?? answerFromOptions[0].label,
                ),
              }}
            />
          </StyledTriggerButton>
        </Dropdown>
        {option.fieldOptionMeta?.optionType === TableDataType.TEXT && (
          <Dropdown
            menu={{
              items: answerLengthOptions.map((lengthOption) => ({
                key: lengthOption.value,
                label: lengthOption.label,
                onClick: () => handleAnswerLengthChange(lengthOption),
              })),
            }}
          >
            <ToolbarDropdownTriggerButton
              icon={<ToolbarButtonIcon icon={faArrowsToLine} />}
            >
              <FormattedMessage
                id="rich_text_editor.sections.type"
                values={{
                  type: startCase(
                    answerLengthOptions.find(
                      (opt) =>
                        opt.value === option.fieldOptionMeta?.answerLength,
                    )?.label ?? answerLengthOptions[0].label,
                  ),
                }}
              />
            </ToolbarDropdownTriggerButton>
          </Dropdown>
        )}
      </Form.Item>
    </DropdownRow>
  );
};
